import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyState } from '@/components/common/empty-state';
import {
  ProductCard,
  ProductCardSkeleton,
  CategoryPills,
  SearchFilterBar,
} from '@/features/mercado/components';
import { useListings } from '@/features/mercado/api';
import { useCondoContext } from '@/features/auth/use-condo-context';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import type { Listing } from '@condo/contracts';

type CategoryId =
  | 'todos'
  | 'muebles'
  | 'electronica'
  | 'electrodomesticos'
  | 'ropa'
  | 'deportes'
  | 'hogar'
  | 'libros'
  | 'juguetes'
  | 'vehiculos'
  | 'otros';

interface Filters {
  query: string;
  priceMin?: number;
  priceMax?: number;
  conditions: Array<'nuevo' | 'como_nuevo' | 'buen_estado' | 'usado'>;
  sameBuildingOnly: boolean;
  sort: 'reciente' | 'precio_asc' | 'precio_desc' | 'popular' | 'cercano';
}

const DEFAULT_FILTERS: Filters = {
  query: '',
  conditions: [],
  sameBuildingOnly: false,
  sort: 'reciente',
};

function applyClientFilters(listings: Listing[], filters: Filters): Listing[] {
  let items = [...listings];

  if (filters.query.trim()) {
    const q = filters.query.toLowerCase();
    items = items.filter(
      (l) =>
        l.title.toLowerCase().includes(q) ||
        (l.description?.toLowerCase().includes(q) ?? false),
    );
  }

  if (filters.priceMin !== undefined) {
    items = items.filter((l) => l.priceCLP >= filters.priceMin!);
  }
  if (filters.priceMax !== undefined) {
    items = items.filter((l) => l.priceCLP <= filters.priceMax!);
  }

  if (filters.conditions.length > 0) {
    items = items.filter((l) => filters.conditions.includes(l.condition));
  }

  switch (filters.sort) {
    case 'precio_asc':
      items.sort((a, b) => a.priceCLP - b.priceCLP);
      break;
    case 'precio_desc':
      items.sort((a, b) => b.priceCLP - a.priceCLP);
      break;
    case 'reciente':
    default:
      items.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      break;
  }

  return items;
}

export function AdminMercadoFeed() {
  const navigate = useNavigate();
  const { condoId } = useCondoContext();

  const [category, setCategory] = React.useState<CategoryId>('todos');
  const [filters, setFilters] = React.useState<Filters>(DEFAULT_FILTERS);

  const { data, isLoading } = useListings({
    category: category !== 'todos' ? category : undefined,
  });

  const listings = data?.items ?? [];
  const filtered = applyClientFilters(listings, filters);

  const allListings = useListings();
  const categoryCounts = React.useMemo(() => {
    const all = allListings.data?.items ?? [];
    const counts: Partial<Record<CategoryId, number>> = { todos: all.length };
    for (const l of all) {
      counts[l.category as CategoryId] = (counts[l.category as CategoryId] ?? 0) + 1;
    }
    return counts;
  }, [allListings.data]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Supervision · Mercado Vecinal"
        title="Mercado Vecinal"
        subtitle="Articulos publicados por los residentes. Vista de solo lectura."
      />

      <CategoryPills
        selected={category}
        onSelect={(id) => setCategory(id as CategoryId)}
        counts={categoryCounts}
        className="mb-4"
      />

      <SearchFilterBar
        filters={filters}
        onFiltersChange={setFilters}
        resultCount={filtered.length}
        className="mb-6"
      />

      {isLoading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="Sin resultados"
          description="No hay articulos que coincidan con tu busqueda. Intenta con otros filtros."
        />
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {filtered.map((listing) => (
            <ProductCard
              key={listing.id}
              id={listing.id}
              imageUrl={listing.photos[0]?.url}
              title={listing.title}
              priceCLP={listing.priceCLP}
              sellerName={listing.sellerName}
              sellerRating={4.5}
              condition={listing.condition}
              createdAt={listing.createdAt}
              isSameBuilding={false}
              condominiumName={listing.condominiumName}
              onClick={() => navigate(`/app/condominios/${condoId}/mercado/${listing.id}`)}
            />
          ))}
        </div>
      )}
    </PageContainer>
  );
}
